import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { formatDate } from '../../utils/dateUtils';
import type { Project } from '../../types/project';
import ProfileAvatar from '../common/ProfileAvatar';
import EyeButton from '../common/EyeButton';
import AlliancesHeadshots from '../alliances/AlliancesHeadshots';
import ReviewModal from '../reviews/ReviewModal';
import BidderDetailsModal from '../quotes/BidderDetailsModal';
import { Quote } from '../../types/quote';
import ScrollableContainer from '../common/ScrollableContainer';
import GridTable from '../common/GridTable';
import { Link } from 'react-router-dom';
import { useProfileRole } from '../../hooks/useProfileRole';
import { useReviewExistence } from '../../hooks/useReviewExists';

interface ProjectsHistoryProps {
  historyProjects: Project[];
  title?: string;
  variant?: 'dashboard' | 'default';
}

const ProjectsHistory: React.FC<ProjectsHistoryProps> = ({
  historyProjects,
  title,
  variant = 'default',
}) => {
  const { t } = useTranslation(['projects', 'common']);
  const [quoteToEvaluate, setQuoteToEvaluate] = useState<Quote | null>(null);

  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  const { isRecruiter } = useProfileRole();

  if (historyProjects.length === 0) {
    return null;
  }

  const ProjectRow: React.FC<{
    project: Project;
    setSelectedQuote: React.Dispatch<React.SetStateAction<Quote | null>>;
    setQuoteToEvaluate: React.Dispatch<React.SetStateAction<Quote | null>>;
  }> = ({ project, setSelectedQuote, setQuoteToEvaluate }) => {
    const reviewedId = isRecruiter ? project.quote.quote_owner.id : project.quote.job.client.id;
    const reviewExists = useReviewExistence(project.quote.id, reviewedId);

    const hasAlliances = project.quote.alliances.length > 0;
    const clientName = project.quote.job.client.company_name || project.quote.job.client.first_name;

    const freelancerName = project.quote.quote_owner.first_name;

    const avatarProfile = isRecruiter ? project.quote.quote_owner : project.quote.job.client;
    const avatarName = isRecruiter ? freelancerName : clientName;

    const projectTitle = isRecruiter ? project.quote.job.title : clientName;
    const projectSubtitle = isRecruiter
      ? t('withClient', { name: freelancerName })
      : project.quote.job.title;

    const projectDate =
      project.quote.job.status === 'completed'
        ? project.quote.job.completed_at
        : project.quote.updated_at;

    return (
      <>
        <GridTable.Cell className="gap-4 pl-0">
          <ProfileAvatar
            profile_id={avatarProfile.id}
            first_name={avatarName}
            avatar_url={avatarProfile.avatar_url}
            className="flex-shrink-0"
          />
          <div className="text-secondary">
            <div className="font-bold leading-tight">{projectTitle}</div>
            <div className="text-sm leading-tight">{projectSubtitle}</div>
          </div>
        </GridTable.Cell>
        <GridTable.Cell className="text-sm">
          {t(`job_statuses.${project.quote.job.status}`, { ns: 'common' })}
        </GridTable.Cell>
        <GridTable.Cell className="text-sm text-gray-600">{formatDate(projectDate)}</GridTable.Cell>
        <GridTable.Cell className="justify-center">
          {hasAlliances ? (
            <AlliancesHeadshots
              alliances={project.quote.alliances.map(alliance => alliance.invited_freelancer)}
              allianceCount={project.quote.alliances.length}
              avatarSize="w-5 h-5"
            />
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </GridTable.Cell>
        <GridTable.Cell className="justify-center">
          <EyeButton
            ariaLabel={t('viewSubmission', { ns: 'projects' })}
            onClick={() => setSelectedQuote(project.quote)}
          />
        </GridTable.Cell>
        <GridTable.Cell className="justify-center md:justify-end md:pr-0">
          {variant === 'dashboard' ? (
            <Button variant="transparent-with-border" href={'/projects'}>
              {t('common:form_actions.manage')}
            </Button>
          ) : (
            !reviewExists && (
              <Button variant="primary" onClick={() => setQuoteToEvaluate(project.quote)}>
                {t('evaluate')}
              </Button>
            )
          )}
        </GridTable.Cell>
      </>
    );
  };

  return (
    <section className="border-2 flex flex-col gap-6 rounded-2xl border-gray-300 bg-white px-6 py-5 shadow-xl md:px-8 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[1.2rem] font-bold text-secondary">{title || t('history')}</h2>
        {variant === 'dashboard' && (
          <Button
            variant="transparent-with-border"
            href="/projects"
            className="ml-auto hidden md:inline-flex"
          >
            {t('dashboard:projects.view_all')}
          </Button>
        )}
      </div>

      <ScrollableContainer>
        <GridTable className="divide-y-[2px] divide-[#E6EDF4]">
          {/* Grid Header */}
          <GridTable.Cell isHeader className="!border-t-0 pl-0">
            {t('description')}
          </GridTable.Cell>
          <GridTable.Cell isHeader className="!border-t-0">
            {t('status')}
          </GridTable.Cell>
          <GridTable.Cell isHeader className="!border-t-0">
            {t('date')}
          </GridTable.Cell>
          <GridTable.Cell isHeader className="!border-t-0 text-center">
            {isRecruiter ? t('team') : t('alliances')}
          </GridTable.Cell>
          <GridTable.Cell isHeader className="!border-t-0 text-center">
            {t('submission')}
          </GridTable.Cell>
          <GridTable.Cell isHeader className="!border-t-0 pr-0">
            {' '}
          </GridTable.Cell>

          {/* Grid Rows */}
          {historyProjects.map(project => (
            <ProjectRow
              key={project.quote.id}
              project={project}
              setSelectedQuote={setSelectedQuote}
              setQuoteToEvaluate={setQuoteToEvaluate}
            />
          ))}
        </GridTable>
      </ScrollableContainer>

      {variant === 'dashboard' && (
        <div className="mt-4 sm:hidden">
          <Link
            to="/projects"
            className="block rounded-full border border-primary px-6 py-2 text-center font-bold text-secondary"
          >
            {t('dashboard:projects.view_all')}
          </Link>
        </div>
      )}

      {quoteToEvaluate && (
        <ReviewModal onClose={() => setQuoteToEvaluate(null)} quote={quoteToEvaluate} />
      )}

      {selectedQuote && (
        <BidderDetailsModal
          onClose={() => setSelectedQuote(null)}
          job={selectedQuote.job}
          quote={selectedQuote}
        />
      )}
    </section>
  );
};

export default ProjectsHistory;
